import { useEffect, useRef } from 'react';
import type { PoolOfferConfig } from '../../lib/api/operatorConsole';
import { CustomMark } from './CustomMark';
import { LevelRtpCell } from './LevelRtpCell';
import { PercentInput } from './PercentInput';
import {
  avgCardValue,
  buybackEffective,
  canSetLevelRtp,
  canSetRtp,
  formatMoney,
  isCustomPack,
  MAX_LOOT_RTP,
  priceLabel,
  pricingPatchForRtp,
  RESTOCKING_HINT,
  RTP_LEVELS,
  rtpEffective,
  samePercent,
  type PricingPatch,
} from './pricing';

type Props = {
  configs: PoolOfferConfig[];
  /** Values most packs share; a pack that differs is marked "custom". */
  defaultRtp: number | null;
  defaultBuyback: number | null;
  /** Resolves to false when the value was refused, so the input shows the saved one again. */
  onSave: (poolOfferId: number, patch: PricingPatch) => Promise<boolean | void>;
  /** Ticked packs — the defaults block then applies to these only. */
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
};

const STATUS_LABELS: Record<string, string> = {
  enabled: 'Live',
  staged: 'Not published',
  restocking: 'Restocking',
};

const th = 'px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-[var(--co-muted)]';
const td = 'px-3 py-2 align-middle';

function SelectAll({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // indeterminate has no HTML attribute; it is a DOM property only.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label="Select all packs"
      className="h-4 w-4 cursor-pointer accent-[var(--co-brand)]"
    />
  );
}

export function PricingTable({
  configs,
  defaultRtp,
  defaultBuyback,
  onSave,
  selectedIds,
  onSelectionChange,
}: Props) {
  const selected = new Set(selectedIds);
  const allSelected = configs.length > 0 && configs.every((c) => selected.has(c.pool_offer_id));

  function toggle(id: number) {
    onSelectionChange(
      selected.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id],
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
      <table className="w-full min-w-[960px] border-collapse text-[13px] text-[var(--co-text)]">
        <thead className="border-b border-[var(--co-border)]">
          <tr>
            <th className={`${th} w-10`}>
              <SelectAll
                checked={allSelected}
                indeterminate={selectedIds.length > 0 && !allSelected}
                onChange={() =>
                  onSelectionChange(allSelected ? [] : configs.map((c) => c.pool_offer_id))
                }
              />
            </th>
            <th className={th}>Pack</th>
            <th className={`${th} text-right`}>Price</th>
            <th className={th} title="RTP of the Normal level, the pack's own RTP">
              RTP Normal
            </th>
            {RTP_LEVELS.map((level) => (
              <th
                key={level.field}
                className={th}
                title={`RTP of the ${level.label} level. Muted = same as Normal.`}
              >
                RTP {level.label}
              </th>
            ))}
            <th className={`${th} text-right`} title="Average value of cards in one pack at the Normal RTP">
              Avg card value
            </th>
            <th className={th}>Buyback</th>
            <th className={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {configs.map((config) => {
            const name = config.title ?? config.pool_offer_title;
            const rtp = rtpEffective(config);
            const buyback = buybackEffective(config);
            const avgValue = avgCardValue(config, rtp);
            const rtpCustom = defaultRtp != null && rtp != null && !samePercent(rtp, defaultRtp);
            const buybackCustom = defaultBuyback != null && !samePercent(buyback, defaultBuyback);

            return (
              <tr
                key={config.pool_offer_id}
                className={`border-b border-[var(--co-border)] last:border-0 ${
                  selected.has(config.pool_offer_id) ? 'bg-[var(--co-bg)]' : ''
                }`}
              >
                <td className={td}>
                  <input
                    type="checkbox"
                    checked={selected.has(config.pool_offer_id)}
                    onChange={() => toggle(config.pool_offer_id)}
                    aria-label={`Select ${name}`}
                    className="h-4 w-4 cursor-pointer accent-[var(--co-brand)]"
                  />
                </td>
                <td className={td}>
                  <div className="font-medium">{name}</div>
                  <div className="font-mono text-[11px] text-[var(--co-muted)]">#{config.pool_offer_id}</div>
                </td>
                {/* A pack with no price to measure RTP against (a $0 one too) shows a dash, as it always has. */}
                <td className={`${td} text-right font-mono`}>{canSetRtp(config) ? priceLabel(config) : '—'}</td>
                <td className={`${td} whitespace-nowrap`}>
                  {canSetRtp(config) ? (
                    <>
                      <PercentInput
                        label={`RTP for ${name}`}
                        value={rtp}
                        {...(isCustomPack(config) ? {} : { max: MAX_LOOT_RTP })}
                        onCommit={(v) => onSave(config.pool_offer_id, pricingPatchForRtp(config, v))}
                      />
                      {rtpCustom && (
                        <CustomMark
                          label={`Reset RTP for ${name} to ${defaultRtp}%`}
                          onReset={() =>
                            void onSave(config.pool_offer_id, pricingPatchForRtp(config, defaultRtp!))
                          }
                        />
                      )}
                    </>
                  ) : (
                    <span className="text-[12px] text-[var(--co-muted)]">No price</span>
                  )}
                </td>
                {RTP_LEVELS.map((level) => (
                  <td key={level.field} className={`${td} whitespace-nowrap`}>
                    {canSetLevelRtp(config) ? (
                      <LevelRtpCell config={config} level={level} name={name} onSave={onSave} />
                    ) : (
                      <span className="text-[12px] text-[var(--co-muted)]">—</span>
                    )}
                  </td>
                ))}
                <td className={`${td} text-right font-mono`}>
                  {avgValue != null ? formatMoney(avgValue) : '—'}
                </td>
                <td className={`${td} whitespace-nowrap`}>
                  <PercentInput
                    label={`Buyback for ${name}`}
                    value={buyback}
                    max={100}
                    onCommit={(v) => onSave(config.pool_offer_id, { buyback_percent: v / 100 })}
                  />
                  {buybackCustom && (
                    <CustomMark
                      label={`Reset buyback for ${name} to ${defaultBuyback}%`}
                      onReset={() =>
                        void onSave(config.pool_offer_id, { buyback_percent: defaultBuyback! / 100 })
                      }
                    />
                  )}
                </td>
                <td
                  className={`${td} text-[12px] text-[var(--co-muted)]`}
                  title={config.availability === 'restocking' ? RESTOCKING_HINT : undefined}
                >
                  {STATUS_LABELS[config.availability] ?? config.availability}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
